import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	formatDateToShow,
	useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CountryService } from "../../services/common/CountryService";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IStatutoryHoliday } from "../../models/statutoryHoliday";
import { StatutoryHolidayInfoSetupFieldsPanel } from "./Panels/StatutoryHolidaySetupInfoSetupFieldsPanel";
import { StatutoryHolidayService } from "../../services/StatutoryHolidayService";
import { StatutoryHolidaySetupToolbar } from "./StatutoryHolidaySetupToolbar";
import { StatutoryHolidaySetupValidationSchema } from "./validator";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";





export interface IStatutoryHolidaySetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "statutory-holiday-single-data-record";
const Statutory_Holiday_LIST_DATA_QUERY_KEY = "statutory-holiday-list";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			return StatutoryHolidayService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useStatutoryHoliday = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [Statutory_Holiday_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {
			return StatutoryHolidayService.getStatutoryHoliday(
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: () => CountryService.getCountryDDL(),
		refetchOnWindowFocus: false,
	});

export const StatutoryHolidaySetup: React.FC<IStatutoryHolidaySetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [statutoryHolidayId, setStatutoryHolidayId] = useState<number | string>(
		0
	);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [provinceDDLItems, setProvinceDDLItems] = useState<any>([]);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "holidayLabel",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "holidayLabel",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useStatutoryHoliday(
		queryParams,
		navigateToLogout
	);

	const statutoryHolidayList: any | null = data;

	const { isPending: isPendingCountryDDL, data: countryList } =
		useGetCountryDDL();

	const { isPending: isSubmitting, mutate: submitStatutoryHolidayFormFields } =
		useMutation({
			mutationFn: StatutoryHolidayService.create,
			onSuccess: (res: IStatutoryHoliday | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/statutory-holiday/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [Statutory_Holiday_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/statutory-holiday/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [Statutory_Holiday_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const upadtedStatutoryHolidayListItems = useMemo(() => {
		return (
			statutoryHolidayList &&
			statutoryHolidayList.items &&
			statutoryHolidayList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/statutory-holiday/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.holidayLabel}
					</Link>
				),
				holidayDate: formatDateToShow(item.holidayDate),
			}))
		);
	}, [statutoryHolidayList]);

	const DEFAULT_VALUES = {
		holidayLabel: undefined,
		holidayDate: null,
		province: undefined,
		regularHoliday: false,
		continuousOperation: false,
		bankHoliday: false,
		optionalHoliday: false,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(StatutoryHolidaySetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {

		const regenrateProvinceDDLItems = provinceDDLItems.map(({ label, value, shortCode }: any) => ({
			name: label,
			id: value,
			shortCode,
		}));


		const selectedProvinceArrayOfObjects = regenrateProvinceDDLItems.filter(
			(item: any) => values.province.includes(item.id)
		);
		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			holidayLabel: values.holidayLabel,
			holidayDate: formatDate(values.holidayDate),
			bankHoliday: values.bankHoliday ? 200 : 100,
			regularHoliday: values.regularHoliday ? 200 : 100,
			continuousOperation: values.continuousOperation ? 200 : 100,
			optionalHoliday: values.optionalHoliday ? 200 : 100,
			province: selectedProvinceArrayOfObjects,
		};
		submitStatutoryHolidayFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "holidayDate") {
					formMethods.setValue(fieldName, new Date(fieldValue) ?? undefined);
				}
				if (fieldName === "province") {
					// Get an array of only the `value` fields
					const provincesValuesArray = fieldValue.map(
						(item: any) => item.id
					);
					formMethods.setValue(fieldName, provincesValuesArray);
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteStatutoryHolidayById } =
		useMutation({
			mutationFn: StatutoryHolidayService.delStatutoryHoliday,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/statutory-holiday`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [Statutory_Holiday_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteStatutoryHolidayField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				statutoryHolidayId: statutoryHolidayId,
				navigateToLogout: navigateToLogout,
			};
			deleteStatutoryHolidayById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setStatutoryHolidayId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setStatutoryHolidayId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveStatutoryHolidayById } =
		useMutation({
			mutationFn: StatutoryHolidayService.archiveStatutoryHoliday,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/statutory-holiday`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [Statutory_Holiday_LIST_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveStatutoryHolidayField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				statutoryHolidayId: statutoryHolidayId,
				navigateToLogout: navigateToLogout,
			};
			archiveStatutoryHolidayById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setStatutoryHolidayId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/statutory-holiday`;
		navigate(path);
	};

	const expandedLabels = ["Misc.", "Statutory Holiday"];
	const expandedStatutoryHolidaySetupOrSetupItem = "Statutory Holiday";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc" &&
				item.label === expandedStatutoryHolidaySetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedStatutoryHolidaySetupOrSetupItem && "/statutory-holiday",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IStatutoryHoliday | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/statutory-holiday/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
			sx={{
				pb: "5rem",
				"& .MuiAppBar-root": {
					zIndex: 2,
					top: subMenuVisibility ? "154px" : "112px",
				},
			}}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteStatutoryHolidayField}
				cancelActionHandler={onCloseHandler}
				testId="statutory-holiday-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Statutory Holiday"
										testId={`statutoryHoliday`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<StatutoryHolidayInfoSetupFieldsPanel
											countryList={countryList ?? []}
											provinceDDLItems={(value: any) =>
												setProvinceDDLItems(value)
											}
											formMethods={formMethods}
										/>
									</StandardPanel>
								}
								toolbar={
									<StatutoryHolidaySetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 2.5, lg: 2.5, xl: 2.5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									statutoryHolidayList && upadtedStatutoryHolidayListItems
										? upadtedStatutoryHolidayListItems
										: []
								}
								columns={[
									{ field: "holidayDate", headerName: "Date" },
									{ field: "regularHoliday", headerName: "Reg" },
									{ field: "continuousOperation", headerName: "Con" },
									{ field: "bankHoliday", headerName: "Bank" },
									{ field: "optionalHoliday", headerName: "Opt" },
									{ field: "label", headerName: "Holiday" },
									{ field: "province", headerName: "Provinces" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									statutoryHolidayList && statutoryHolidayList.total
										? statutoryHolidayList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveStatutoryHolidayField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"statutory-holiday-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
